import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import MuxPlayer from "@mux/mux-player-react/lazy";
import type { Tokens } from "@mux/mux-player";
import MuxPlayerElement from "@mux/mux-player";
import {
  OakP,
  OakFlex,
  OakImage,
  OakUiRoleToken,
} from "@oaknational/oak-components";

import useVideoTracking, {
  VideoAnalyticsOverrides,
  VideoTrackingGetState,
} from "./useVideoTracking";
import getTimeElapsed from "./getTimeElapsed";
import getSubtitleTrack from "./getSubtitleTrack";
import getDuration from "./getDuration";
import {
  useSignedVideoToken,
  useSignedThumbnailToken,
  useSignedStoryboardToken,
} from "./useSignedVideoToken";
import { onPause, onPlay, onTimeUpdate } from "./videoCallbacks";

import theme, { OakColorName } from "@/styles/theme";
import errorReporter from "@/common-lib/error-reporter";
import { VideoLocationValueType } from "@/browser-lib/avo/Avo";
import OakError from "@/errors/OakError";
import { PupilPathwayData } from "@/context/PupilLessonAnalytics/pupilAnalyticsHelpers";
import { AnalyticsBrowseData } from "@/components/TeacherComponents/types/lesson.types";

const INITIAL_DEBUG = false;
const INITIAL_ENV_KEY = process.env.MUX_ENVIRONMENT_KEY;
const RELOAD_ATTEMPTS = 5;

export type VideoStyleConfig = {
  controls: {
    primary: OakColorName;
    secondary: OakColorName;
    tertiary: OakColorName;
  };
};

type VideoPlayerBaseProps = {
  playbackId: string;
  initialStartTime?: number;
  thumbnailTime?: number | null;
  poster?: string;
  title: string;
  location: VideoLocationValueType;
  userEventCallback?: (event: VideoEventCallbackArgs) => void;
  pathwayData?: PupilPathwayData | AnalyticsBrowseData;
  isAudioClip?: boolean;
  loadingTextColor?: OakUiRoleToken;
  defaultHiddenCaptions?: boolean;
  cloudinaryUrl?: string | null;
  muxAssetId?: string | null;
  /** When true, focuses the play button when the player is mounted */
  autoFocusPlayButton?: boolean;
  /** When true, starts playback as soon as the player is ready. */
  autoPlay?: boolean;
  /** When false, pauses playback */
  isActive?: boolean;
  /** When false, suppresses the analytics event for reaching the end. */
  shouldTrackEndAnalytics?: boolean;
  omitBorder?: boolean;
  /** Used to override the functions used to track analytics events  */
  analyticsOverrides?: VideoAnalyticsOverrides;
};

type SignedVideoPlayerProps = VideoPlayerBaseProps & {
  playbackPolicy: "signed";
  /** Refers to whether legacy signing tokens are required or not for signed videos */
  isLegacy: boolean;
};
type PublicVideoPlayerProps = VideoPlayerBaseProps & {
  playbackPolicy: "public";
  /** Signed tokens are not required for public videos */
  isLegacy?: never;
};

export type VideoPlayerProps = SignedVideoPlayerProps | PublicVideoPlayerProps;

export type VideoEventCallbackArgs = {
  event: "play" | "playing" | "pause" | "end";
  timeElapsed: number | null;
  duration: number | null;
  muted: boolean;
};

/**
 * Recursively searches for the play button within the player element's shadow DOM.
 * MuxPlayer uses Media Chrome;
 * Play button is nested in: mux-player > media-theme > media-controller > media-play-button
 */
function findPlayButtonInShadowRoots(
  root: DocumentFragment | Element,
): HTMLElement | null {
  const playButton = root.querySelector("media-play-button");
  if (playButton instanceof HTMLElement) {
    return playButton;
  }
  const children = root.querySelectorAll("*");
  for (const child of children) {
    if (child.shadowRoot) {
      const found = findPlayButtonInShadowRoots(child.shadowRoot);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Focuses the play button within the player element's shadow DOM.
 */
function focusPlayButton(el: MuxPlayerElement) {
  if (!el.shadowRoot) return;

  findPlayButtonInShadowRoots(el.shadowRoot)?.focus({
    // `focusVisible` forces the appearance of the focus ring even
    // when the browser doesn't think the element needs it.
    // this is a very new feature (Landed in Chrome 145, Feb 2026),
    // so this is a progressive enhancement.
    focusVisible: true,
  } as FocusOptions);
}

function VideoContainer({
  omitBorder,
  children,
}: Readonly<{ omitBorder: boolean; children: React.ReactNode }>) {
  return (
    <OakFlex
      data-chromatic="ignore"
      $alignItems={"center"}
      $justifyContent={"center"}
      $ba={omitBorder ? "border-solid-none" : "border-solid-l"}
      $minWidth={"100%"}
      $borderColor={"border-primary"}
      style={{
        aspectRatio: "16/9",
        boxSizing: "content-box",
      }}
    >
      {children}
    </OakFlex>
  );
}

const VideoPlayer: FC<VideoPlayerProps> = (props) => {
  const {
    thumbnailTime: thumbTime,
    poster,
    initialStartTime = 0,
    title,
    location,
    playbackId,
    playbackPolicy,
    userEventCallback = () => {},
    pathwayData,
    isAudioClip,
    loadingTextColor = "text-primary",
    defaultHiddenCaptions = false,
    cloudinaryUrl,
    muxAssetId,
    autoFocusPlayButton = false,
    autoPlay = false,
    isActive = true,
    shouldTrackEndAnalytics = true,
    omitBorder = false,
    analyticsOverrides,
  } = props;

  const isLegacy = playbackPolicy === "signed" ? props.isLegacy : false;

  const mediaElRef = useRef<MuxPlayerElement | null>(null);
  const [endTracked, setEndTracked] = useState<string | null>(null);

  const setMediaElRef = useCallback(
    (el: MuxPlayerElement | null) => {
      mediaElRef.current = el;
      if (autoFocusPlayButton && el) {
        setTimeout(() => focusPlayButton(el), 100);
      }
    },
    [autoFocusPlayButton],
  );
  const [envKey] = useState(INITIAL_ENV_KEY);
  const [debug] = useState(INITIAL_DEBUG);
  const [videoIsPlaying, setVideoIsPlaying] = useState(false);
  const [reloadOnErrors, setReloadOnErrors] = useState<number[]>([]);

  useEffect(() => {
    if (!isActive) {
      mediaElRef.current?.pause();
    }
  }, [isActive]);

  const getState: VideoTrackingGetState = () => {
    const captioned = Boolean(getSubtitleTrack(mediaElRef));
    const duration = getDuration(mediaElRef);
    const muted = Boolean(mediaElRef.current?.muted);
    const timeElapsed = getTimeElapsed(mediaElRef);

    return {
      captioned,
      duration,
      muted,
      playbackId,
      timeElapsed,
      title,
      location: location,
    };
  };

  const videoTracking = useVideoTracking({
    getState,
    pathwayData,
    cloudinaryUrl,
    muxAssetId,
    analyticsOverrides,
  });

  const thumbnailToken = useSignedThumbnailToken({
    playbackId,
    playbackPolicy,
    isLegacy,
  });

  const videoToken = useSignedVideoToken({
    playbackId: playbackId,
    playbackPolicy: playbackPolicy,
    isLegacy,
  });

  const storyboardToken = useSignedStoryboardToken({
    playbackId: playbackId,
    playbackPolicy: playbackPolicy,
    isLegacy,
  });

  const metadata = {
    "metadata-video-id": playbackId,
    "metadata-video-title": title,
    // do we want to track here using user id here?
    // "metadata-viewer-user-id": userId
  };

  const reportError = errorReporter("VideoPlayer.tsx");

  const PLAYING_CLASSNAME = "playing";

  /**
   * Check if the provided error event is a network error. If it is, check the
   * response code to determine if it should be ignored. This is because some
   * network errors are expected and can be handled gracefully.
   *
   * @param evt - the error event
   */
  const checkNetworkError = (
    evt: Event,
  ): { isNetworkError: boolean; ignore?: boolean } => {
    const networkError =
      (evt as CustomEvent).detail?.data?.type === "networkError";

    if (networkError) {
      const networkErrorCode = (evt as CustomEvent).detail?.data?.response
        ?.code;

      if (networkErrorCode === 403) {
        return { isNetworkError: true, ignore: false };
      } else {
        return { isNetworkError: true, ignore: true };
      }
    }

    return { isNetworkError: false };
  };

  const onError = (evt: Event) => {
    const networkError = checkNetworkError(evt);
    if (networkError.ignore) {
      return;
    }

    const newReloadOnErrors = [...reloadOnErrors];
    const prevReloadOnErrorsLength = reloadOnErrors.length;
    if (newReloadOnErrors.length < RELOAD_ATTEMPTS) {
      const timeElapsed = getTimeElapsed(mediaElRef) || 0;
      newReloadOnErrors.push(timeElapsed || 0);
      setReloadOnErrors(newReloadOnErrors);
    }

    const hasMaxAttempts = newReloadOnErrors.length === RELOAD_ATTEMPTS;
    const isFirstAttempt = newReloadOnErrors.length === 1;
    const willReload = prevReloadOnErrorsLength !== newReloadOnErrors.length;

    // Only report the first and last error (ignoring all network errors)
    const shouldReport =
      !networkError.isNetworkError &&
      (hasMaxAttempts || (willReload && (isFirstAttempt || hasMaxAttempts)));

    if (!shouldReport) {
      return;
    }

    const originalError = evt instanceof CustomEvent ? evt.detail : evt;
    const error = new OakError({
      code:
        newReloadOnErrors.length < RELOAD_ATTEMPTS
          ? "video/unknown"
          : "video/persistent-unknown",
      originalError,
      meta: metadata,
    });

    reportError(error, { ...getState() });
  };

  if (videoToken.loading || thumbnailToken.loading || storyboardToken.loading) {
    return (
      <VideoContainer omitBorder={omitBorder}>
        <OakP $color={loadingTextColor} $textAlign="center">
          Loading...
        </OakP>
      </VideoContainer>
    );
  }

  const tokens: Tokens = {
    playback: videoToken?.playbackToken ? videoToken.playbackToken : undefined,
    thumbnail: !isAudioClip
      ? thumbnailToken?.playbackToken
        ? thumbnailToken.playbackToken
        : undefined
      : undefined,
    storyboard: storyboardToken?.playbackToken
      ? storyboardToken.playbackToken
      : undefined,
  };

  const reloadingDueToErrors = reloadOnErrors.length > 0;
  let startTime = Math.max(0, initialStartTime);

  if (reloadingDueToErrors) {
    startTime = reloadOnErrors[reloadOnErrors.length - 1] || 0;
  }

  return (
    <VideoContainer omitBorder={omitBorder}>
      <MuxPlayer
        key={reloadOnErrors.length}
        preload="metadata"
        autoPlay={autoPlay}
        ref={setMediaElRef}
        envKey={envKey}
        metadata={metadata}
        playbackId={playbackId}
        playbackRates={[0.5, 0.7, 1, 1.2, 1.5, 1.7, 2]}
        start-time={startTime}
        tokens={tokens}
        thumbnailTime={thumbTime || undefined}
        poster={poster}
        customDomain={"video.thenational.academy"}
        beaconCollectionDomain={"mux-litix.thenational.academy"}
        debug={debug}
        primaryColor={theme.colors.white}
        secondaryColor={theme.colors.black}
        accentColor={theme.colors.black}
        onPlay={() =>
          onPlay({
            mediaElRef,
            setVideoIsPlaying,
            trackOnPlay: videoTracking.onPlay,
            userEventCallback,
            playingClassname: PLAYING_CLASSNAME,
          })
        }
        onPause={() =>
          onPause({
            mediaElRef,
            setVideoIsPlaying,
            trackOnPause: videoTracking.onPause,
            userEventCallback,
            playingClassname: PLAYING_CLASSNAME,
          })
        }
        onError={onError}
        onTimeUpdate={() =>
          onTimeUpdate({
            mediaElRef,
            setEndTracked,
            trackOnEnd: videoTracking.onEnd,
            userEventCallback,
            playbackId,
            endTracked,
            shouldTrackEndAnalytics,
            playingClassname: PLAYING_CLASSNAME,
          })
        }
        onCanPlay={() => {
          if (reloadingDueToErrors && videoIsPlaying) {
            mediaElRef.current?.play();
          }
        }}
        defaultHiddenCaptions={defaultHiddenCaptions}
        style={{
          aspectRatio: "16/9",
          overflow: "hidden",
        }}
      >
        {poster ? (
          <OakImage
            slot="poster"
            src={poster}
            alt=""
            $width="100%"
            $height="100%"
            $objectFit="cover"
            $background="bg-inverted"
          />
        ) : null}
      </MuxPlayer>
    </VideoContainer>
  );
};

export default VideoPlayer;
